import {ExistingTaxIdentificationNumber} from 'Agent/Policies/models/TaxIdentificationNumber';
import {Loaded} from 'Core/models/Async';
import {ValidEmail} from 'Core/models/Email';
import {NonEmptyString} from 'Core/models/EmptyString';
import {Page} from 'Core/models/Page';
import {ValidPhoneNumber} from 'Core/models/PhoneNumber';
import {RoleId} from 'Core/models/Role';
import {Right} from 'fp-ts/lib/Either';
import {Agent} from 'http';
import {addFilterParameters} from 'Shared/utilities/addFilterParameters';
import {get, post, put} from 'Shared/utilities/fetchHelpers';
import {AgentUserFilter} from 'Users/ducks/agentUser';
import {AgencySupportVisibilityPermission} from 'Users/models/AgencyAssociation';
import {AgentMappingPayload, ValidAgentMapping} from 'Users/models/AgentMapping';
import {AgentUser} from 'Users/models/AgentUser';
import {UserModel} from 'Users/models/UserModel';

type RequestAgentAccessParams = {
  requestingAgentId: number;
  agentId: number;
};
type RemoveAgentAccessParams = {
  agentId: number;
  agentIdToRemove: number;
};

export const mapVisibilityPermission = (permission: AgencySupportVisibilityPermission) => {
  return permission === AgencySupportVisibilityPermission.Account
    ? RoleId.AgencySupportWithAccounts
    : permission === AgencySupportVisibilityPermission.Policies
    ? RoleId.AgencySupportWithPolicies
    : permission === AgencySupportVisibilityPermission.CommissionsAnd1099s
    ? RoleId.AgencySupportWithCommissionsAnd1099s
    : permission === AgencySupportVisibilityPermission.PolicyServiceRequest
    ? RoleId.AgencySupportWithPolicyServiceRequest
    : permission === AgencySupportVisibilityPermission.Correspondence
    ? RoleId.AgencySupportWithCorrespondence
    : permission === AgencySupportVisibilityPermission.Claims
    ? RoleId.AgencySupportWithClaims
    : permission === AgencySupportVisibilityPermission.UnderwritingRequirements
    ? RoleId.AgencySupportWithUnderwritingRequirements
    : [];
};

export const agentRootUri = '/agents';
export default {
  approveAccess: (requestingAgentId: number, approvingAgentId: number) =>
    get(`${agentRootUri}/${approvingAgentId}/approveaccess/${requestingAgentId}`),
  completeRegistration: (username: string) =>
    put(`${agentRootUri}/agents/key/username`, {
      username,
    }),
  createAgent: (mapping: ValidAgentMapping, isSupport: boolean, id?: string, encryptedIdentifier?: string) => {
    const agentMapping = {
      agencyAssociations: mapping.value.agencyAssociations.map(association => ({
        agentNumbers: association.agentNumbers.map(({isNewYork, value}) => ({
          isNewYork,
          value,
        })),
        roleIds: isSupport
          ? [
              ...(association.visibilityPermissions.length > 0
                ? association.visibilityPermissions.map(mapVisibilityPermission)
                : []),
            ]
          : [
              ...(association.isAdministrator ? [RoleId.AgencyAdministrator] : []),
              ...(association.agentNumbers.length > 0 ? [RoleId.Producer] : []),
            ],
        identifier:
          association.taxIdNumber instanceof ExistingTaxIdentificationNumber ? null : association.taxIdNumber.value,
        encryptedIdentifier:
          association.taxIdNumber instanceof ExistingTaxIdentificationNumber ? association.taxIdNumber.value : null,
      })),
      email: (mapping.value.email as Loaded<Right<ValidEmail>>).Data.right.address,
      firstName: mapping.value.firstName.value,
      id,
      lastName: mapping.value.lastName.value,
      mobilePhone: (mapping.value.phoneNumber as Loaded<Right<ValidPhoneNumber>>).Data.right.phoneNumber,
      userName: (mapping.value.userName as Loaded<Right<NonEmptyString>>).Data.right.value,
    };

    const isAgencyAdministrator = Boolean(encryptedIdentifier);
    const updateRoute = isAgencyAdministrator
      ? `/users/agency/agent/${agentMapping.userName}${addFilterParameters({
          encryptedIdentifier,
        })}`
      : '/users/agents';
    const updatePayload = isAgencyAdministrator
      ? {
          roleIds: agentMapping.agencyAssociations[0].roleIds,
          email: agentMapping.email,
          agentNumbers: agentMapping.agencyAssociations[0].agentNumbers,
        }
      : agentMapping;

    return id ? put(updateRoute, updatePayload) : post(`/users/agents/exclusive`, agentMapping);
  },
  denyAccess: (requestingAgentId: number, rejectingAgentId: number) =>
    get(`${agentRootUri}/${rejectingAgentId}/denyaccess/${requestingAgentId}`),
  getAgent: (agentId: string, encryptedIdentifier?: string) =>
    get<AgentMappingPayload>(`/users/agents/${agentId}/details${addFilterParameters({encryptedIdentifier})}`),
  getAgentByEmail: (key: string) => get<AgentUser>(`${agentRootUri}/key/${key}`),
  getAgentByUserName: (userName: string) => get<Agent>(`${agentRootUri}/${userName}`),
  getConnections: (agentId: number) => get<Agent[]>(`${agentRootUri}/${agentId}/connectedagents`),
  listAgents: () => get<Page<Agent>>(`${agentRootUri}/`),
  listUsers: () => get<Page<UserModel>>(`users`),
  offboardAgent: (agentId: number) => post<Agent>(`${agentRootUri}/${agentId}/offboard`),
  removeAgentAccess: (removeAccessParams: RemoveAgentAccessParams) =>
    get(`${agentRootUri}/${removeAccessParams.agentIdToRemove}/removeaccess/${removeAccessParams.agentId}`),
  requestAgentAccess: (requestAccessParams: RequestAgentAccessParams) =>
    get(`${agentRootUri}/${requestAccessParams.agentId}/requestAccess/${requestAccessParams.requestingAgentId}`),
  search: (searchString: string) => get<Agent[]>(`${agentRootUri}/search/${searchString}`),
  searchAgentUsers: (filter?: AgentUserFilter) =>
    get<Page<UserModel>>(`/users/agents${addFilterParameters({...filter})}`),
  updateAgentUsername: (key: string, username: NonEmptyString) =>
    put<Agent>(`${agentRootUri}/updateUsername`, {
      key,
      username: username.value,
    }),
};
